import { Text, Toaster, useToaster } from "@vagastec/ensemble-core";
import AvatarEditor from "react-avatar-editor";
import { useDropzone } from "react-dropzone";
import { Button } from "./components/button";
import {
  btnEditApresentacao,
  inputVariants,
  primaryButton,
} from "./components/button.css";
import { getCookie } from "./cookies";
import { extractTextFromHtmlString } from "./extract-text-from-html-string";
import { Fieldset } from "./components/fieldset";
import { IconeImage } from "./icons/icone-image";
import { InfoIcon } from "./icons/info-icon";
import { Input } from "./components/input";
import {
  errorMessageIconStyle,
  errorMessageStyle,
} from "./components/input-base.css";
import {
  infoMessageIconStyle,
  infoMessageStyle,
} from "./components/input-info-message.css";
import { RadioButton } from "./components/radio-button";
import { renderSelectOptions } from "./render-select-options";
import { Select } from "./components/select";
import { Space } from "./components/space";
import { Spinner } from "./components/spinner";
import { avatarEditorContainerStyle, containerLogoStyle } from "./step-1.css";
import { TextEditor } from "./components/text-editor";
import { vars } from "./theme.css";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";

export function FieldsetSobreEmpresa({
  formState,
  setFormState,
  isVagaModelo,
  hasTriedToSubmit,
}) {
  const [logoScale, setLogoScale] = React.useState(1);
  const logoEditorRef = React.useRef();
  const [logoErrorMessage, setLogoErrorMessage] = React.useState(null);
  const [isPostPending, setIsPostPending] = React.useState(false);
  const [toasts, addToast, removeToast] = useToaster();
  const apresentacaoDescricaoTextLength = extractTextFromHtmlString(
    formState.apresentacaoDescricao
  ).length;

  const intl = useIntl();
  const dropzoneErrorCodes = {
    "file-invalid-type": intl.formatMessage(
      defineMessage({
        id: "sobreEmpresa.erroFormatoArquivo",
        defaultMessage: "O formato do arquivo deve ser jpeg ou png.",
      })
    ),

    "file-too-large": intl.formatMessage(
      defineMessage({
        id: "sobreEmpresa.erroTamanhoArquivo",
        defaultMessage: "A imagem inserida deve ser menor do que 150kb.",
      })
    ),
  };

  function setApresentacaoDataFromApi() {
    const apresentacaoEmpresa = formState.apresentacoesEmpresaData.find(
      (v) => v.id === formState.apresentacaoId
    );

    fetch(apresentacaoEmpresa?.logotipo)
      .then((res) => res.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.addEventListener("load", () => {
          setFormState({
            apresentacaoLogoAsDataUrlOriginal: reader.result,
          });
        });
      });

    setFormState({
      apresentacaoNomeEmpresa: apresentacaoEmpresa.nome_empresa,
      apresentacaoNomeModelo: apresentacaoEmpresa.nome_template,
      apresentacaoLogo: apresentacaoEmpresa.logotipo,
      apresentacaoDescricao: apresentacaoEmpresa.descricao,
    });
  }

  const onDropAccepted = ([file]) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener("load", () => {
      const image = new Image();
      image.src = reader.result;
      image.addEventListener("load", () => {
        if (image.width < 200 || image.height < 200) {
          setLogoErrorMessage(
            intl.formatMessage(
              defineMessage({
                id: "sobreEmpresa.dimensoesImagem",
                defaultMessage:
                  "A altura e a largura da imagem devem ter no mínimo 200px.",
              })
            )
          );
          return;
        }
        setLogoErrorMessage(null);
        setFormState({
          apresentacaoLogoAsDataUrlOriginal: reader.result,
        });
      });
    });
  };

  const onDropRejected = ([item]) => {
    // console.log(item);
    setLogoErrorMessage(dropzoneErrorCodes[item.errors[0]?.code]);
    // item.errors?.map((error) => console.log(dropzoneErrorCodes[error.code]));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept: "image/jpeg, image/png",
    maxSize: 150000, // bytes
    maxFiles: 1,
  });

  function addPostErrorToast() {
    const title = intl.formatMessage(
      defineMessage({
        id: "mensagem.ocorreuErro",
        defaultMessage: "Ocorreu um erro",
      })
    );

    const body = intl.formatMessage(
      defineMessage({
        id: "sobreEmpresa.postBodyErro",
        defaultMessage: "Não foi possível salvar a apresentação da empresa",
      })
    );

    const variant = "error";
    const onDismiss = (id) => removeToast(id);
    const timer = "max";

    addToast({
      title,
      variant,
      body,
      timer,
      onDismiss,
    });
  }

  function addPostSuccessToast() {
    const title = intl.formatMessage(
      defineMessage({
        id: "post.titleSucesso",
        defaultMessage: "Sucesso!",
      })
    );

    const body = intl.formatMessage(
      defineMessage({
        id: "sobreEmpresa.postBodySucesso",
        defaultMessage: "A apresentação da empresa foi salva com sucesso.",
      })
    );

    const variant = "success";
    const onDismiss = (id) => removeToast(id);
    const timer = "max";

    addToast({
      title,
      variant,
      body,
      timer,
      onDismiss,
    });
  }

  function handlePost() {
    const TOKEN = getCookie(process.env.ACCESS_TOKEN);

    setIsPostPending(true);
    fetch(`${process.env.HESTIA}/apresentacao_cliente`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${TOKEN}`,
      },
      method: "POST",
      body: JSON.stringify({
        descricao: formState.apresentacaoDescricao,
        logotipo:
          formState.apresentacaoLogoAsDataUrlEdited ??
          formState.apresentacaoLogoAsDataUrlOriginal,
        nome_empresa: formState.apresentacaoNomeEmpresa,
        nome_template: formState.apresentacaoNomeModelo,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        addPostSuccessToast();
        return res.json();
      })
      .then((json) => {
        setFormState((prev) => ({
          apresentacaoId: json.id,
          apresentacoesEmpresaData: [...prev.apresentacoesEmpresaData, json],
        }));
      })
      .catch(() => {
        addPostErrorToast();
      })
      .finally(() => {
        setIsPostPending(false);
      });
  }

  function handlePut() {
    const TOKEN = getCookie(process.env.ACCESS_TOKEN);

    setIsPostPending(true);
    fetch(
      `${process.env.HESTIA}/apresentacao_cliente/${formState.apresentacaoId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${TOKEN}`,
        },
        method: "PUT",
        body: JSON.stringify({
          descricao: formState.apresentacaoDescricao,
          logotipo: formState.apresentacaoLogoAsDataUrlEdited,
          nome_empresa: formState.apresentacaoNomeEmpresa,
          nome_template: formState.apresentacaoNomeModelo,
        }),
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error();
        addPostSuccessToast();
        return res.json();
      })
      .then((json) => {
        setFormState((prev) => ({
          apresentacoesEmpresaData: prev.apresentacoesEmpresaData.map((v) =>
            v.id === json.id ? json : v
          ),
        }));
      })
      .catch(() => {
        addPostErrorToast();
      })
      .finally(() => {
        setIsPostPending(false);
      });
  }

  return (
    <>
      <Fieldset
        title={
          <FormattedMessage
            id="sobreEmpresa.titleVagaConfidencial"
            defaultMessage="Este anúncio é confidencial?"
          />
        }
        isValid={formState.isAnuncioConfidencial !== null || isVagaModelo}
        shouldValidate={hasTriedToSubmit}
        errorMessage={
          <FormattedMessage
            id="erro.campoObrigatorio"
            defaultMessage="Campo obrigatório"
          />
        }
      >
        <RadioButton
          label={<FormattedMessage id="radioButton.Sim" defaultMessage="Sim" />}
          name="exibe-apresentacao"
          value={true}
          checked={formState.isAnuncioConfidencial}
          onChange={(e) => {
            if (e.target.value) {
              setFormState({
                tipoApresentacao: "",
                apresentacaoNomeModelo: "",
                apresentacaoNomeEmpresa: "",
                apresentacaoDescricao: "",
                apresentacaoLogo: "",
                apresentacaoId: "",
              });
            }
            setFormState({ isAnuncioConfidencial: true });
          }}
          required={!isVagaModelo}
          data-testid="radio-sim"
        />

        <RadioButton
          label={<FormattedMessage id="radioButton.Nao" defaultMessage="Não" />}
          name="exibe-apresentacao"
          value={false}
          checked={formState.isAnuncioConfidencial === false}
          onChange={(e) => {
            if (e.target.value) {
              setFormState({ tipoApresentacao: "" });
            }
            setFormState({ isAnuncioConfidencial: false });
          }}
          data-testid="radio-nao"
        />
      </Fieldset>

      {formState.isAnuncioConfidencial === false ? (
        <>
          <Space />
          <Fieldset
            title={
              <FormattedMessage
                id="sobreEmpresa.ComoQuerApresentarEmpresa"
                defaultMessage="Como quer apresentar sua empresa?"
              />
            }
            isValid={formState.tipoApresentacao}
            shouldValidate={hasTriedToSubmit}
            errorMessage={
              <FormattedMessage
                id="erro.campoObrigatorio"
                defaultMessage="Campo obrigatório"
              />
            }
          >
            <RadioButton
              label={
                <FormattedMessage
                  id="sobreEmpresa.criandoModelo"
                  defaultMessage="Criando um modelo"
                />
              }
              name="tipo-apresentacao"
              value="criando-modelo"
              checked={formState.tipoApresentacao === "criando-modelo"}
              onChange={(e) => {
                setFormState({
                  isEditingApresentacao: false,
                  apresentacaoNomeModelo: "",
                  apresentacaoNomeEmpresa: "",
                  apresentacaoDescricao: "",
                  apresentacaoLogo: "",
                });

                if (e.target.value) {
                  setFormState({ apresentacaoId: "" });
                }
                setFormState({ tipoApresentacao: e.target.value });
              }}
              data-testid="radio-criando-modelo"
            />
            {formState.apresentacoesEmpresaData.length ? (
              <RadioButton
                label={
                  <FormattedMessage
                    id="sobreEmpresa.utilizandoModelo"
                    defaultMessage="Utilizando um modelo"
                  />
                }
                name="tipo-apresentacao"
                value="utilizando-modelo"
                checked={formState.tipoApresentacao === "utilizando-modelo"}
                onChange={(e) => {
                  if (e.target.value) {
                    setFormState({
                      apresentacaoNomeModelo: "",
                      apresentacaoNomeEmpresa: "",
                      apresentacaoDescricao: "",
                      apresentacaoLogo: "",
                    });
                  }
                  setFormState({ tipoApresentacao: e.target.value });
                }}
                required={
                  formState.isAnuncioConfidencial === false && !isVagaModelo
                }
                data-testid="radio-utilizando-modelo"
              />
            ) : null}
          </Fieldset>

          {formState.tipoApresentacao === "utilizando-modelo" &&
          !formState.isEditingApresentacao ? (
            <>
              <Space />

              <Select
                label={
                  <FormattedMessage
                    id="sobreEmpresa.selectLabelModelo"
                    defaultMessage="Modelo"
                    description="O termo modelo é sempre referente a um template"
                  />
                }
                value={formState.apresentacaoId}
                onChange={(e) =>
                  setFormState({
                    apresentacaoId: Number(e.target.value),
                  })
                }
                required
                shouldValidate={hasTriedToSubmit}
                errorMessage={
                  <FormattedMessage
                    id="erro.campoObrigatorio"
                    defaultMessage="Campo obrigatório"
                  />
                }
                data-testid="select-modelo-apresentacao"
              >
                {renderSelectOptions({
                  data: formState.apresentacoesEmpresaData,
                  error: formState.apresentacoesEmpresaError,
                  labelKey: "nome_template",
                  errorMessage: intl.formatMessage(
                    defineMessage({
                      id: "select.erro",
                      defaultMessage: "Ocorreu um erro",
                    })
                  ),
                  loadingMessage: intl.formatMessage(
                    defineMessage({
                      id: "select.loading",
                      defaultMessage: "Carregando...",
                    })
                  ),
                  selectOptionMessage: intl.formatMessage(
                    defineMessage({
                      id: "select.escolherOpcao",
                      defaultMessage: "Selecione uma opção...",
                    })
                  ),
                })}
              </Select>

              <Space />
            </>
          ) : null}

          {formState.tipoApresentacao === "utilizando-modelo" &&
          formState.apresentacaoId &&
          !formState.isEditingApresentacao ? (
            <Button
              className={btnEditApresentacao}
              onClick={() => {
                setApresentacaoDataFromApi();
                // setIsEditingApresentacao(true);
                setFormState({ isEditingApresentacao: true });
              }}
              data-testid="btn-editar"
            >
              <Text weight={600}>
                {
                  <FormattedMessage
                    id="sobreEmpresa.editarModelo"
                    defaultMessage="Editar modelo"
                  />
                }
              </Text>
            </Button>
          ) : null}

          {formState.tipoApresentacao === "criando-modelo" ||
          formState.isEditingApresentacao ? (
            <>
              {formState.isEditingApresentacao ? (
                <Button
                  className={btnEditApresentacao}
                  onClick={() => {
                    setApresentacaoDataFromApi();
                    setFormState({
                      isEditingApresentacao: false,
                      apresentacaoNomeModelo: "",
                      apresentacaoNomeEmpresa: "",
                      apresentacaoDescricao: "",
                      apresentacaoLogo: "",
                    });
                  }}
                  data-testid="btn-cancelar"
                >
                  <Text weight={600}>
                    <FormattedMessage
                      id="sobreEmpresa.cancelarEdicao"
                      defaultMessage="Cancelar edição"
                    />
                  </Text>
                </Button>
              ) : null}

              <Space />

              <Input
                label={
                  <FormattedMessage
                    id="sobreEmpresa.modelo"
                    defaultMessage="Dê um nome para este modelo"
                  />
                }
                type="text"
                placeholder={intl.formatMessage(
                  defineMessage({
                    id: "step1.placeholderNomeModelo",
                    defaultMessage:
                      "O nome facilitará a posterior reutilização da apresentação",
                  })
                )}
                value={formState.apresentacaoNomeModelo}
                onChange={(e) =>
                  setFormState({
                    apresentacaoNomeModelo: e.target.value,
                  })
                }
                required
                shouldValidate={hasTriedToSubmit}
                errorMessage={
                  <FormattedMessage
                    id="erro.campoObrigatorio"
                    defaultMessage="Campo obrigatório"
                  />
                }
                data-testid="input-nome-modelo"
              />

              <Space />

              <Input
                label={
                  <FormattedMessage
                    id="sobreEmpresa.empresa"
                    defaultMessage="Empresa"
                  />
                }
                type="text"
                placeholder={intl.formatMessage(
                  defineMessage({
                    id: "step1.placeholderNomeEmpresa",
                    defaultMessage: "Qual o nome da sua empresa?",
                  })
                )}
                value={formState.apresentacaoNomeEmpresa}
                onChange={(e) =>
                  setFormState({
                    apresentacaoNomeEmpresa: e.target.value,
                  })
                }
                required
                shouldValidate={hasTriedToSubmit}
                errorMessage={
                  <FormattedMessage
                    id="erro.campoObrigatorio"
                    defaultMessage="Campo obrigatório"
                  />
                }
                data-testid="input-nome-empresa"
              />

              <Space />
              <Text as="h2" weight={600}>
                <FormattedMessage
                  id="sobreEmpresa.logotipo"
                  defaultMessage="Logotipo"
                />
              </Text>
              <Space height=".5rem" />

              {/* TODO: verificar performance */}

              <div className={containerLogoStyle} {...getRootProps()}>
                <input
                  {...getInputProps()}
                  data-testid="btn-escolher-arquivo"
                  id="btn-imagem"
                  name="btn-imagem"
                />
                <IconeImage
                  fill={vars.color.lightForeground}
                  height="40px"
                  width="40px"
                />
                <Text size="md" weight={600}>
                  <FormattedMessage
                    id="sobreEmpresa.adicionarImagem"
                    defaultMessage="Adicionar imagem"
                  />
                </Text>
                <Text size="sm" as="p" fontColor={vars.color.lightForeground}>
                  <FormattedMessage
                    id="sobreEmpresa.infoUpload"
                    defaultMessage="Arraste o arquivo ou clique aqui para adicionar uma imagem"
                  />
                </Text>
              </div>
              <div className={infoMessageStyle}>
                <InfoIcon
                  width={16}
                  height={16}
                  className={infoMessageIconStyle}
                ></InfoIcon>
                <Text size="sm" weight={600} as="p">
                  <FormattedMessage
                    id="sobreEmpresa.infoFormatos"
                    defaultMessage="Formatos possíveis: jpeg e png. As dimensões mínimas são
                    200px/200px. O tamanho máximo é de 150kb."
                  />
                </Text>
              </div>
              {logoErrorMessage ? (
                <div className={errorMessageStyle}>
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className={errorMessageIconStyle}
                  >
                    <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm4.207 12.793l-1.414 1.414L12 13.414l-2.793 2.793-1.414-1.414L10.586 12 7.793 9.207l1.414-1.414L12 10.586l2.793-2.793 1.414 1.414L13.414 12l2.793 2.793z" />
                  </svg>
                  <Text size="sm" weight={600} as="p">
                    {logoErrorMessage}
                  </Text>
                </div>
              ) : null}

              {formState.apresentacaoLogoAsDataUrlOriginal ? (
                <div className={avatarEditorContainerStyle}>
                  <AvatarEditor
                    ref={logoEditorRef}
                    width={200}
                    height={200}
                    image={formState.apresentacaoLogoAsDataUrlOriginal}
                    scale={logoScale}
                    onImageReady={() => {
                      const scaledImageAsDataUrl = logoEditorRef.current
                        ?.getImageScaledToCanvas()
                        ?.toDataURL();

                      setFormState({
                        apresentacaoLogoAsDataUrlEdited: scaledImageAsDataUrl,
                      });
                    }}
                    onImageChange={() => {
                      const scaledImageAsDataUrl = logoEditorRef.current
                        ?.getImageScaledToCanvas()
                        ?.toDataURL();

                      setFormState({
                        apresentacaoLogoAsDataUrlEdited: scaledImageAsDataUrl,
                      });
                    }}
                  />

                  <input
                    type="range"
                    name="logo-image-scale"
                    step={0.01}
                    min={0.1}
                    max={2}
                    value={logoScale}
                    onChange={(e) => setLogoScale(Number(e.target.value))}
                  />
                </div>
              ) : null}

              <Space />

              <TextEditor
                label={
                  <FormattedMessage
                    id="sobreEmpresa.labelApresentacao"
                    defaultMessage="Descrição da empresa"
                  />
                }
                value={formState.apresentacaoDescricao}
                placeholder={intl.formatMessage(
                  defineMessage({
                    id: "step1.placeholderDescricaoEmpresa",
                    defaultMessage:
                      "Descreva a missão, ambiente de trabalho, valores e outros diferenciais",
                  })
                )}
                onChange={(e) =>
                  setFormState({
                    apresentacaoDescricao: e,
                  })
                }
                isImageHidden
                isVideoHidden
                isVariablesHidden
                length={apresentacaoDescricaoTextLength}
                isValid={
                  apresentacaoDescricaoTextLength &&
                  apresentacaoDescricaoTextLength < 4000
                }
                shouldValidate={hasTriedToSubmit}
                errorMessage={
                  <FormattedMessage
                    id="sobreEmpresa.limiteDescricao"
                    defaultMessage="Campo obrigatório. Máximo de 4000 caracteres."
                  />
                }
                infoMessage={
                  <FormattedMessage
                    id="sobreEmpresa.infoDescricao"
                    defaultMessage="Máximo de 4000 caracteres."
                  />
                }
                data-testid="editor-descricao"
                isVariablesHidden
              />

              <Space height="0.5rem" />

              <Button
                className={`${primaryButton} ${inputVariants["form-buttons"]}`}
                type="submit"
                onClick={() => {
                  formState.tipoApresentacao === "criando-modelo"
                    ? handlePost()
                    : handlePut();
                }}
                data-testid="btn-salvar-apresentacao"
                disabled={isPostPending}
              >
                {isPostPending ? (
                  <Spinner colorVariant="light" />
                ) : (
                  <FormattedMessage id="botao.salvar" defaultMessage="Salvar" />
                )}
              </Button>
            </>
          ) : null}
        </>
      ) : null}
      <Toaster toasts={toasts} />
    </>
  );
}
